import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { formatGapParts } from 'src/app/_helpers/currency';
import {
  InlineBadgeComponent,
  InlineBadgeVariant,
} from 'src/app/shared/components/inline-badge/inline-badge.component';

/**
 * Renders a signed gap as a coloured amount plus a percentage badge. A `null`
 * gap shows a dash; the percentage badge is omitted when it rounds to zero.
 */
@Component({
  selector: 'app-gap-display',
  standalone: true,
  imports: [InlineBadgeComponent],
  template: `
    @if (parts(); as parts) {
      <span class="gap-cell">
        <span
          [class.gap-positive]="parts.direction === 'positive'"
          [class.gap-negative]="parts.direction === 'negative'"
        >
          {{ parts.amountText }}
        </span>
        @if (parts.percentText; as percentText) {
          <app-inline-badge
            [badgeContent]="percentText"
            [variant]="variant()"
          />
        }
      </span>
    } @else {
      <span>–</span>
    }
  `,
  styles: `
    :host {
      display: inline-flex;
      align-items: center;
    }
    .gap-cell {
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }
    .gap-positive {
      color: #ef4444;
    }
    .gap-negative {
      color: #16a34a;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GapDisplayComponent {
  readonly gap = input<{ amount: number; pct: number } | null>(null);

  readonly parts = computed(() => {
    const gap = this.gap();
    return gap ? formatGapParts(gap.amount, gap.pct) : null;
  });

  readonly variant = computed<InlineBadgeVariant>(() =>
    this.parts()?.direction === 'positive' ? 'error' : 'success'
  );
}
